import React, { useCallback, useEffect, useState } from "react";
import { Translations } from "../../../models/translations";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { TransportOrderInvoicingState, TransportOrderListItemDto, TransportOrderListQueryParameters } from "../../../models/transport/transportOrder";
import { useParams } from "react-router-dom";
import { TransportOrderFilters } from "./TransportOrderFilters";
import { InvoicingTransportOrderList } from "./InvoicingTransportOrderList";
import { showErrorMessage, showSuccessMessage } from "../../../models/store/storeActions";
import { InvoiceCreateDto } from "../../../models/transportOrderInvoice/invoiceCreateDto";
import { useAppDispatch } from "../../../framework/customStore";
import { customHistory } from "../../../framework/customHistory";
import { showApiError } from "../../framework/formUtils";
import { addTransportOrdersToInvoice, createNewInvoice } from "../../../services/newInvoiceService";
import { fetchTransportOrderInvoice } from "../../../store/transportOrderInvoiceSlice";
import dayjs from "dayjs";
import { getTransportOrders } from "../../../services/transportOrderService";
import { LoadingIndicator } from "../../framework/loadingIndicatorNew";
import { Box } from "@mui/system";
import { DEFAULT_ORDERS_TIMERANGE } from "./InvoicingTransportOrdersMain";

interface TransportOrderInvoiceRowDialogProps {
    open: boolean;
    title: string;
    onClose: () => void;
    selectedInvoiceCustomerId?: string;
}

export const TransportOrderInvoiceRowDialog = ({ open, title, onClose, selectedInvoiceCustomerId }: TransportOrderInvoiceRowDialogProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [orderItems, setOrderItems] = useState<TransportOrderListItemDto[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<TransportOrderListItemDto[]>([]);
    const { id } = useParams<{ id: string }>();
    const newInvoiceData: InvoiceCreateDto = {
        reference: "",
        customerId: selectedOrders[0]?.billingCustomerId,
        invoiceMessage: "",
    };
    const [timeRange, setTimeRange] = useState<[Date, Date]>([
        dayjs().startOf("date").subtract(DEFAULT_ORDERS_TIMERANGE, "days").toDate(),
        dayjs().endOf("date").toDate(),
    ]);
    const [keyword, setKeyword] = useState<string>("");

    // Pagination
    const [page, setPage] = useState(0); // 0-based
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    const [filterCustomer, setFilterCustomer] = useState<string>("");
    useEffect(() => {
        setFilterCustomer(selectedInvoiceCustomerId);
    }, [selectedInvoiceCustomerId]);

    // Fetch orders
    const showOnlyInvoiceable = true;
    const fetchOrders = useCallback(async() => {
        try {
            const qsp: TransportOrderListQueryParameters = {
                ...(filterCustomer && { billingCustomerId: filterCustomer }),
                ...(keyword && { keyword }),
                page: page + 1, // API expects 1-based
                pageSize: rowsPerPage,
                ...(showOnlyInvoiceable
                    ? {
                        ...(timeRange[0] && { actualDeliveryStart: timeRange[0].toISOString() }),
                        ...(timeRange[1] && { actualDeliveryEnd: timeRange[1].toISOString() }),
                        invoicingState: TransportOrderInvoicingState.Invoiceable,
                    }
                    : {
                        ...(timeRange[0] && { timeFrameStartDate: timeRange[0].toISOString() }),
                        ...(timeRange[1] && { timeFrameEndDate: timeRange[1].toISOString() }),
                    }),
            };
            const res = await getTransportOrders(qsp);
            if (res?.data) {
                setOrderItems(res.data.items);
                setTotalCount(res.data.totalCount || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [showOnlyInvoiceable, filterCustomer, timeRange, keyword, page, rowsPerPage]);

    useEffect(() => {
        setLoading(true);
        void fetchOrders();
    }, [fetchOrders]);

    const handleKeywordChange = (val: string) => {
        setKeyword(val);
        setPage(0);
    };
    const handleTimeRangeChange = (val: [Date, Date]) => {
        setTimeRange(val);
        setPage(0);
    };
    const handleFilterCustomerChange = (val: string) => {
        setFilterCustomer(val);
        setPage(0);
    };

    const handleAddSelected = () => {
        if (id === "new") {
            createNewInvoice(newInvoiceData)
                .then(res => {
                    if (res) {
                        handleAddTransportOrdersToInvoice(res.data.id);
                    } else {
                        dispatch(showErrorMessage(Translations.FailedToCreateInvoice));
                    }
                }).catch(showApiError);
        } else {
            handleAddTransportOrdersToInvoice(id);
        }
    };

    const handleAddTransportOrdersToInvoice = (id: string) => {
        const transportOrders = selectedOrders.map(i => i.id);
        addTransportOrdersToInvoice(id, transportOrders)
            .then(response => {
                if (response) {
                    dispatch(showSuccessMessage(Translations.AddTransportOrdersToInvoiceSuccess));
                    customHistory.push(
                        `/invoicingbeta/transportorderinvoice/${id}`,
                        { from: "/invoicingbeta/transportorderinvoice/new" }
                    );
                    dispatch(fetchTransportOrderInvoice(id) as any);
                    onClose();
                } else {
                    dispatch(showErrorMessage(Translations.AddTransportOrdersToInvoiceFailed));
                }
            }).catch(() => {
                dispatch(
                    showErrorMessage(Translations.AddTransportOrdersToInvoiceFailed)
                );
            });
    };

    return (
        <Dialog
            open={open}
            title={title}
            onClose={onClose}
            fullWidth
            maxWidth={"xl"}
        >
            <DialogTitle>
                <Grid2 container justifyContent="space-between" alignItems="center">
                    <Typography variant="h2">{title}</Typography>
                    <Button color="primary" endIcon={<CloseIcon />} onClick={onClose} />
                </Grid2>
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: "350px" }}>
                <TransportOrderFilters
                    filterCustomer={""}
                    setFilterCustomer={handleFilterCustomerChange}
                    timeRange={timeRange}
                    setTimeRange={handleTimeRangeChange}
                    setKeyword={handleKeywordChange}
                />
                {loading && (
                    <Box sx={{
                        position: "absolute", inset: 0, display: "flex",
                        alignItems: "center", justifyContent: "center",
                        background: "rgba(255,255,255,0.7)", zIndex: 10
                    }}
                    >
                        <LoadingIndicator />
                    </Box>
                )}
                {!loading && (
                <InvoicingTransportOrderList
                    orderItems={orderItems}
                    selectedOrders={selectedOrders}
                    setSelectedOrders={setSelectedOrders}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    totalCount={totalCount}
                />
                )}
            </DialogContent>
            <DialogActions className="mx-3 my-2">
                <Button
                    color="success"
                    variant={"contained"}
                    disabled={selectedOrders.length <= 0}
                    onClick={handleAddSelected}
                >
                    {Translations.AddSelected}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
